import React from "react";

/**
 * Button — the one button component for the Boardroom.
 *
 *   primary   — the single CTA per view. Core Blue.
 *   secondary — reversible secondary path. White + border + blue label.
 *   ghost     — row-level / low-stakes (dismiss, export).
 *   accent    — data actions: sync, refresh, reconnect.
 *   danger    — irreversible, confirm-gated.
 *
 * Never introduce a one-off button style outside this set — see the
 * Brand Package "Which button, when" table.
 */
const VARIANTS = {
  primary:   "bg-blue-800 text-white border border-transparent hover:bg-blue-900 active:bg-blue-950",
  secondary: "bg-white text-blue-800 border border-ink-300 hover:bg-blue-50 hover:border-blue-300",
  ghost:     "bg-transparent text-ink-700 border border-transparent hover:bg-ink-100 hover:text-ink-900",
  accent:    "bg-cyan-700 text-white border border-transparent hover:bg-cyan-800",
  danger:    "bg-status-critical text-white border border-transparent hover:bg-status-critical-text",
};

const SIZES = {
  sm: "h-8 px-3.5 text-[12.5px] rounded-sm gap-1.5",
  md: "h-10 px-[18px] text-[14px] rounded gap-2",
  lg: "h-12 px-6 text-[15.5px] rounded-lg gap-2",
};

export default function Button({
  variant = "primary",
  size = "md",
  className = "",
  children,
  disabled,
  testId,
  ...props
}) {
  return (
    <button
      data-testid={testId}
      disabled={disabled}
      className={[
        "inline-flex items-center justify-center font-sans font-semibold transition-colors duration-150",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-sky focus-visible:ring-offset-2",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        VARIANTS[variant] || VARIANTS.primary,
        SIZES[size] || SIZES.md,
        className,
      ].join(" ")}
      {...props}
    >
      {children}
    </button>
  );
}
